import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents, fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Search } from "lucide-react";
import { MONTHS } from "@/components/bizstats/KpiTab";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ReasonDialog = ({ target, onClose, onConfirm, pending }) => {
  const [note, setNote] = useState("");
  useEffect(() => { setNote(""); }, [target]);
  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="bs-exclude-dialog">
        <DialogHeader><DialogTitle>Εξαίρεση από το P&L</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6F6A60]">{target?.label}</p>
        <textarea data-testid="bs-exclude-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} rows={3} autoFocus
          placeholder="Λόγος (π.χ. εικονικό, επανάληψη, προσωπικό έξοδο)…" className={`${inp} w-full`} />
        <DialogFooter>
          <button data-testid="bs-exclude-cancel" onClick={onClose} className={inp}>Άκυρο</button>
          <button data-testid="bs-exclude-confirm" disabled={pending} onClick={() => onConfirm(note)}
            className="rounded-xl bg-[#45423C] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Εξαίρεση</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Note = ({ text, testid }) => (text ? <div data-testid={testid} className="text-[11px] italic text-[#B5452F] mt-0.5">Λόγος: {text}</div> : null);

const inp = "rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C]";
const KEYS = ["erp-suppliers", "erp-docs", "erp-stats", "erp-kpis"];

const useRefresh = () => {
  const qc = useQueryClient();
  return () => { KEYS.forEach((k) => qc.invalidateQueries({ queryKey: [k] })); qc.invalidateQueries(); };
};

const Toggle = ({ on, onChange, disabled, testid }) => (
  <button data-testid={testid} disabled={disabled} onClick={onChange}
    className={`relative h-6 w-11 rounded-full transition-colors disabled:opacity-50 ${on ? "bg-[#6E7A63]" : "bg-[#D9D3C7]"}`}>
    <span className={`absolute left-0 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const Totals = ({ d, testid }) => (
  <div className="grid grid-cols-3 gap-3" data-testid={testid}>
    {[["Σύνολο SoftOne", d?.total], ["Μετράει στο P&L", d?.included], ["Εξαιρείται", (d?.total || 0) - (d?.included || 0)]].map(([l, v]) => (
      <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
        <div className="font-serif text-xl mt-1 text-[#45423C] tabular">{fmtEuro(v || 0)}</div>
      </div>
    ))}
  </div>
);

const SuppliersPanel = ({ year, canEdit, onPick }) => {
  const refresh = useRefresh();
  const [q, setQ] = useState("");
  const [ask, setAsk] = useState(null);
  const { data } = useQuery({ queryKey: ["erp-suppliers", year], queryFn: async () => (await api.get(`/erp/suppliers?year=${year}`)).data });
  const { data: lines = [] } = useQuery({ queryKey: ["erp-pl-lines"], queryFn: async () => (await api.get("/erp/pl-lines")).data });
  const save = useMutation({
    mutationFn: async (body) => (await api.put("/erp/suppliers/map", body)).data,
    onSuccess: () => { refresh(); setAsk(null); toast.success("Το P&L ενημερώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const rows = (data?.rows || []).filter((r) => !q || r.supplier.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="bs-suppliers-panel">
      <Totals d={data} testid="bs-suppliers-totals" />
      <div className="relative w-72">
        <Search className="h-4 w-4 absolute left-3 top-2.5 text-[#6F6A60]" />
        <input data-testid="bs-supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Προμηθευτής…" className={`${inp} pl-9 w-full`} />
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-3 py-2 text-left">Στο P&L</th><th className="px-3 text-left">Προμηθευτής</th><th className="px-3 text-right">Παραστ.</th><th className="px-3 text-right">Σύνολο</th><th className="px-3 text-right">Μετράει</th><th className="px-3 text-left">Γραμμή P&L</th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.supplier} className={`border-t border-[#E3DED4] ${r.excluded ? "opacity-50" : ""}`} data-testid={`bs-supplier-row-${i}`}>
                <td className="px-3 py-2"><Toggle testid={`bs-supplier-toggle-${i}`} on={!r.excluded} disabled={!canEdit || save.isPending} onChange={() => (r.excluded ? save.mutate({ supplier: r.supplier, excluded: false }) : setAsk({ supplier: r.supplier, label: r.supplier }))} /></td>
                <td className="px-3 max-w-[320px]">
                  <button data-testid={`bs-supplier-open-${i}`} onClick={() => onPick(r.supplier)} className="text-left font-medium text-[#45423C] hover:underline truncate max-w-full">{r.supplier}</button>
                  <div className="text-[11px] text-[#6F6A60] truncate">SoftOne: {r.softone_categories.join(", ")}{r.excluded_docs ? ` · ${r.excluded_docs} παραστ. εξαιρούνται` : ""}</div>
                  {r.excluded && <Note text={r.note} testid={`bs-supplier-note-${i}`} />}
                </td>
                <td className="px-3 text-right tabular">{r.docs}</td>
                <td className="px-3 text-right tabular">{fmtEuroCents(r.total)}</td>
                <td className="px-3 text-right tabular font-semibold">{fmtEuroCents(r.included)}</td>
                <td className="px-3">
                  <select data-testid={`bs-supplier-line-${i}`} disabled={!canEdit || save.isPending} value={r.pl_code}
                    onChange={(e) => save.mutate({ supplier: r.supplier, category_code: e.target.value })} className={`${inp} py-1.5 text-xs max-w-[220px]`}>
                    {lines.map((l) => <option key={l.code} value={l.code}>{l.code} {l.label}</option>)}
                  </select>
                  <div className="text-[10px] text-[#6F6A60] mt-0.5">{r.mapping === "manual" ? "επιλογή σας" : "αυτόματα"}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ReasonDialog target={ask} pending={save.isPending} onClose={() => setAsk(null)}
        onConfirm={(note) => save.mutate({ supplier: ask.supplier, excluded: true, note })} />
    </div>
  );
};

export const DocsPanel = ({ year, canEdit, supplier, setSupplier }) => {
  const refresh = useRefresh();
  const [qIn, setQIn] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => { setQ(qIn); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [qIn]);
  const [month, setMonth] = useState(0);
  const [status, setStatus] = useState("all");
  const [kind, setKind] = useState("all");
  const [page, setPage] = useState(1);
  const [ask, setAsk] = useState(null);
  const params = { year, q, supplier, month, status, kind, page };
  const { data } = useQuery({ queryKey: ["erp-docs", params], queryFn: async () => (await api.get("/erp/docs", { params })).data });
  const flag = useMutation({
    mutationFn: async (body) => (await api.put("/erp/docs/flags", body)).data,
    onSuccess: () => { refresh(); setAsk(null); toast.success("Το P&L ενημερώθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const set = (fn) => (e) => { fn(e.target.value); setPage(1); };
  return (
    <div className="space-y-4" data-testid="bs-docs-panel">
      <Totals d={data} testid="bs-docs-totals" />
      <div className="flex flex-wrap gap-3 items-center">
        <input data-testid="bs-docs-search" value={qIn} onChange={(e) => setQIn(e.target.value)} placeholder="Προμηθευτής, αρ. παραστατικού, κατηγορία…" className={`${inp} w-72`} />
        <select data-testid="bs-docs-month" value={month} onChange={set((v) => setMonth(Number(v)))} className={inp}>
          <option value={0}>Όλοι οι μήνες</option>{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select data-testid="bs-docs-kind" value={kind} onChange={set(setKind)} className={inp}>
          <option value="all">Αγορές + Δαπάνες</option><option value="purchases">Αγορές</option><option value="expenses">Δαπάνες</option>
        </select>
        <select data-testid="bs-docs-status" value={status} onChange={set(setStatus)} className={inp}>
          <option value="all">Όλα</option><option value="included">Μετράνε στο P&L</option><option value="excluded">Εξαιρούνται</option>
        </select>
        {supplier && <button data-testid="bs-docs-clear-supplier" onClick={() => { setSupplier(""); setPage(1); }} className="rounded-full bg-[#45423C] text-white text-xs px-3 py-1.5">{supplier} ✕</button>}
        <span className="text-xs text-[#6F6A60]" data-testid="bs-docs-count">{data?.count || 0} παραστατικά</span>
      </div>
      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F0EA] text-[10px] uppercase tracking-wider text-[#6F6A60]">
            <tr><th className="px-3 py-2 text-left">Στο P&L</th><th className="px-3 text-left">Ημ/νία</th><th className="px-3 text-left">Παραστατικό</th><th className="px-3 text-left">Προμηθευτής</th><th className="px-3 text-left">Κατηγορία SoftOne</th><th className="px-3 text-left">Γραμμή P&L</th><th className="px-3 text-right">Καθαρή αξία</th></tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((d, i) => (
              <tr key={d.key} className={`border-t border-[#E3DED4] ${d.excluded ? "opacity-50" : ""}`} data-testid={`bs-doc-row-${i}`}>
                <td className="px-3 py-2">
                  <Toggle testid={`bs-doc-toggle-${i}`} on={!d.excluded} disabled={!canEdit || flag.isPending || d.excluded === "supplier" || d.excluded === "category"}
                    onChange={() => (d.excluded ? flag.mutate({ keys: [d.key], excluded: false }) : setAsk({ key: d.key, label: `${d.doc_no} · ${d.supplier} · ${fmtEuroCents(d.net)}` }))} />
                  {d.excluded === "supplier" && <div className="text-[10px] text-[#6F6A60] mt-0.5">εξαιρείται ο προμηθευτής</div>}
                  {d.excluded === "category" && <div className="text-[10px] text-[#6F6A60] mt-0.5">εξαιρείται το είδος</div>}
                </td>
                <td className="px-3 whitespace-nowrap">{new Date(d.date).toLocaleDateString("el-GR")}</td>
                <td className="px-3 whitespace-nowrap">{d.doc_no}<div className="text-[11px] text-[#6F6A60]">{d.doc_type}</div></td>
                <td className="px-3 max-w-[240px]"><div className="truncate" title={d.supplier}>{d.supplier}</div><Note text={d.excluded ? d.note : ""} testid={`bs-doc-note-${i}`} /></td>
                <td className="px-3 text-xs">{d.category}{d.expense_type ? <div className="text-[11px] text-[#6F6A60]">{d.expense_type}</div> : null}</td>
                <td className="px-3 text-xs">{d.pl_line}</td>
                <td className={`px-3 text-right tabular whitespace-nowrap ${d.net < 0 ? "text-[#6E7A63]" : ""}`}>{fmtEuroCents(d.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.pages > 1 && (
        <div className="flex items-center gap-3 text-sm" data-testid="bs-docs-pager">
          <button data-testid="bs-docs-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className={`${inp} disabled:opacity-40`}>‹</button>
          <span>Σελίδα {page} / {data.pages}</span>
          <button data-testid="bs-docs-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className={`${inp} disabled:opacity-40`}>›</button>
        </div>
      )}
      <ReasonDialog target={ask} pending={flag.isPending} onClose={() => setAsk(null)}
        onConfirm={(note) => flag.mutate({ keys: [ask.key], excluded: true, note })} />
    </div>
  );
};

export const PLFilters = ({ year, canEdit }) => {
  const refresh = useRefresh();
  const [ask, setAsk] = useState(null);
  const [sup, setSup] = useState("");
  const { data: cats = [] } = useQuery({ queryKey: ["erp-categories", year], queryFn: async () => (await api.get(`/erp/categories?year=${year}`)).data });
  const { data: sups } = useQuery({ queryKey: ["erp-suppliers", year], queryFn: async () => (await api.get(`/erp/suppliers?year=${year}`)).data });
  const done = { onSuccess: () => { refresh(); setAsk(null); setSup(""); toast.success("Το P&L ενημερώθηκε"); }, onError: (e) => toast.error(apiErr(e)) };
  const catM = useMutation({ mutationFn: async (b) => (await api.put("/erp/categories/map", b)).data, ...done });
  const supM = useMutation({ mutationFn: async (b) => (await api.put("/erp/suppliers/map", b)).data, ...done });
  const excludedSups = (sups?.rows || []).filter((s) => s.excluded);
  if (!cats.length) return null;
  const chip = (on) => `inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${on ? "bg-white border-[#E3DED4] text-[#45423C] hover:border-[#45423C]" : "bg-[#F6E3DC] border-[#E9C3B6] text-[#B5452F] line-through"}`;
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-5 space-y-4" data-testid="pl-filters">
      <div>
        <h2 className="font-medium text-[#45423C]">Φίλτρα P&L — εξαίρεση είδους εξόδου ή προμηθευτή</h2>
        <p className="text-xs text-[#6F6A60] mt-1">Πατήστε ένα είδος (π.χ. owner) για να μη μετράει στο P&L. Ξαναπατήστε για να επανέλθει.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {cats.map((c, i) => (
          <button key={c.category} data-testid={`pl-filter-cat-${i}`} disabled={!canEdit || catM.isPending} className={chip(!c.excluded)} title={c.note || ""}
            onClick={() => (c.excluded ? catM.mutate({ category: c.category, excluded: false }) : setAsk({ category: c.category, label: `${c.category} · ${fmtEuro(c.total)}` }))}>
            {c.category} <span className="font-normal tabular">{fmtEuro(c.total)}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="pl-filter-supplier" value={sup} disabled={!canEdit} onChange={(e) => setSup(e.target.value)} className={`${inp} py-1.5 text-xs max-w-[320px]`}>
          <option value="">Εξαίρεση προμηθευτή…</option>
          {(sups?.rows || []).filter((s) => !s.excluded).map((s) => <option key={s.supplier} value={s.supplier}>{s.supplier} · {fmtEuro(s.total)}</option>)}
        </select>
        <button data-testid="pl-filter-supplier-add" disabled={!sup || !canEdit} onClick={() => setAsk({ supplier: sup, label: sup })}
          className="rounded-xl bg-[#45423C] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">Εξαίρεση</button>
        {excludedSups.map((s, i) => (
          <button key={s.supplier} data-testid={`pl-filter-sup-${i}`} disabled={!canEdit || supM.isPending} className={chip(false)} title={s.note || ""}
            onClick={() => supM.mutate({ supplier: s.supplier, excluded: false })}>{s.supplier} ✕</button>
        ))}
      </div>
      <ReasonDialog target={ask} pending={catM.isPending || supM.isPending} onClose={() => setAsk(null)}
        onConfirm={(note) => (ask.category ? catM.mutate({ category: ask.category, excluded: true, note }) : supM.mutate({ supplier: ask.supplier, excluded: true, note }))} />
    </div>
  );
};

export const DocsTab = ({ year, canEdit }) => {
  const [view, setView] = useState("suppliers");
  const [supplier, setSupplier] = useState("");
  return (
    <div className="space-y-5" data-testid="bs-docs-tab">
      {!canEdit && <p className="text-xs text-[#B5452F]" data-testid="bs-docs-readonly">Μόνο προβολή — αλλαγές κάνουν ο ιδιοκτήτης και το HR.</p>}
      <p className="text-sm text-[#6F6A60]">Τα έξοδα του P&L για το {year} έρχονται από τα παραστατικά SoftOne (καθαρά ποσά). Κλείστε τον διακόπτη για να μη μετράει ένας προμηθευτής ή ένα παραστατικό. Οι επενδύσεις και τα έξοδα ιδιοκτήτη πηγαίνουν κάτω από το EBITDA.</p>
      <div className="flex gap-2">
        {[["suppliers", "Ανά προμηθευτή"], ["docs", "Όλα τα παραστατικά"]].map(([k, l]) => (
          <button key={k} data-testid={`bs-docs-view-${k}`} onClick={() => setView(k)}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${view === k ? "bg-[#C86235] text-white" : "bg-white border border-[#E3DED4] text-[#45423C]"}`}>{l}</button>
        ))}
      </div>
      {view === "suppliers"
        ? <SuppliersPanel year={year} canEdit={canEdit} onPick={(s) => { setSupplier(s); setView("docs"); }} />
        : <DocsPanel year={year} canEdit={canEdit} supplier={supplier} setSupplier={setSupplier} />}
    </div>
  );
};
